import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { useForm } from '@abyss/web/hooks/useForm';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { phoneOnly } from '../../../../../../common/ConstantsStyles';
import { getCurrentDateWithDashFormatting } from '../../../../../../common/Utils/datesUtils';
import { useSendEmail } from '../../../../../../hooks/useSendEmail';
import { emailRegex } from '../utils';
import { ShareDrawerButtons } from './ShareDrawerButtons';
import {
  EmailLayoutStyles,
  EmailMessageTextStyles,
  EmailValidationErrorStyles,
  StyledDiv,
} from './ShareDrawerContents.styled';
import { ShareDrawerEmailInputs } from './ShareDrawerEmailInputs';
import { ConstantsLagoon } from '../../../../../../common/ConstantsLagoon';
import find from 'lodash/find';
import { useLagoon } from '../../../../../../hooks/useLagoon/useLagoon';

type Props = {
  openShareContents?: boolean;
  setOpenShareContents?: (value: boolean) => void;
  handleSendEmail: () => Promise<void>;
};

export const SendEmailForProviderList = ({
  openShareContents: openShareContentsProp,
  setOpenShareContents: setOpenShareContentsProp,
  handleSendEmail: handleSendEmailDesktop
}: Props) => {
  const { t } = useTranslation();
  const mobile = useMediaQuery(phoneOnly);
  const form = useForm();
  const [, sendEmail] = useSendEmail({});

  const [internalOpenShareContents, setInternalOpenShareContents] = useState(true);
  const [emailOne, setEmailOne] = useState('');
  const [emailTwo, setEmailTwo] = useState('');
  const [emailThree, setEmailThree] = useState('');
  const [emailFour, setEmailFour] = useState('');
  const [emailFive, setEmailFive] = useState('');
  const [emailError, setEmailError] = useState('');

   const lagoonUIMessage = useLagoon('ui-messaging')();
   const emailDirectoryMsgText = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.EMAIL_DIRECTORY_MSG,
    })?.message;
   const emailDirectoryValidMsgText = find(lagoonUIMessage, {
      key: ConstantsLagoon.GUEST_PLAN_SELECTION.EMAIL_DIRECTORY_VALID_EMAIL_MSG,
    })?.message;

  const openShareContents =
    openShareContentsProp ?? internalOpenShareContents;
  const setOpenShareContents =
    setOpenShareContentsProp ?? setInternalOpenShareContents;

  const formatEmails = () => {
    const emails = [emailOne];
    if (emailTwo) emails.push(emailTwo);
    if (emailThree) emails.push(emailThree);
    if (emailFour) emails.push(emailFour);
    if (emailFive) emails.push(emailFive);
    return emails;
  };

  const validateAndSend = async () => {
    const emails = formatEmails();
    const hasInvalidEmail = emails.some((email) => !emailRegex.test(email.trim()));
    if (!emails.length || !emailOne.trim()) return;
    if (hasInvalidEmail) {
      setEmailError(emailDirectoryValidMsgText);
      return;
    }
    setEmailError('');

    const subject = `${t('SHARE_PROVIDERS.PROVIDER')} ${t(
      'SHARE_PROVIDERS.SEARCH_ON'
    )} ${getCurrentDateWithDashFormatting()}`;

    const htmlText = `<div>
      <p>${subject}</p>
      <p>${t('SHARE_DRAWER_TEXT.SHARE_ALL_RESULTS')}</p>
      <p><a href="${window.location.href}">${window.location.href}</a></p>
    </div>`;

    await sendEmail({
      variables: {
        emails,
        subject,
        text: '',
        htmlText,
      },
    });
    await handleSendEmailDesktop();
  };

  if (!openShareContents) return null;

  return (
    <FormProvider
      data-auto-testid="share-email-form"
      data-testid="share-email-form"
      onSubmit={() => undefined}
      state={form}
    >
      {mobile ? (
        <Layout.Stack css={EmailLayoutStyles} grow>
          <StyledDiv>
            <Text
              css={EmailMessageTextStyles}
            >
              {emailDirectoryMsgText}
            </Text>
            <ShareDrawerEmailInputs
              emailFive={emailFive}
              emailFour={emailFour}
              emailOne={emailOne}
              emailThree={emailThree}
              emailTwo={emailTwo}
              hideAddAnotherEmailLink={true}
              setEmailFive={setEmailFive}
              setEmailFour={setEmailFour}
              setEmailOne={setEmailOne}
              setEmailThree={setEmailThree}
              setEmailTwo={setEmailTwo}
            />
            {emailError && (
              <Text
                css={EmailValidationErrorStyles}
                data-testid="email-validation-error"
              >
                {emailError}
              </Text>
            )}
            <ShareDrawerButtons
              handleSendEmailDesktop={validateAndSend}
              openShareContents={openShareContents}
              setOpenShareContents={setOpenShareContents}
            />
          </StyledDiv>
        </Layout.Stack>
      ) : (
        <Layout.Group
          alignItems="top"
          css={EmailLayoutStyles}
          grow
        >
          <StyledDiv>
            <Text
              css={EmailMessageTextStyles}
            >
              {emailDirectoryMsgText}
            </Text>
            <ShareDrawerEmailInputs
              emailFive={emailFive}
              emailFour={emailFour}
              emailOne={emailOne}
              emailThree={emailThree}
              emailTwo={emailTwo}
              hideAddAnotherEmailLink={true}
              setEmailFive={setEmailFive}
              setEmailFour={setEmailFour}
              setEmailOne={setEmailOne}
              setEmailThree={setEmailThree}
              setEmailTwo={setEmailTwo}
            />
            {emailError && (
              <Text
                css={EmailValidationErrorStyles}
                data-testid="email-validation-error"
              >
                {emailError}
              </Text>
            )}
            <ShareDrawerButtons
              handleSendEmailDesktop={validateAndSend}
              openShareContents={openShareContents}
              setOpenShareContents={setOpenShareContents}
            />
          </StyledDiv>
        </Layout.Group>
      )}
    </FormProvider>
  );
};
